import logUpdate from 'log-update';
import chalk from 'chalk';

export type StepStatus = 'in_progress' | 'ok' | 'failed' | 'skipped';

export interface Step {
  step: string;
  status: StepStatus;
  elapsed_ms?: number | null;
  started_at?: string;
  code?: string;
  error?: string;
  // Arbitrary per-step context fields (e.g. namespace, services, pending).
  [k: string]: unknown;
}

const SPINNER_FRAMES = ['⠋','⠙','⠹','⠸','⠼','⠴','⠦','⠧','⠇','⠏'];

// Builder-facing labels for the server's deploy-step names. The raw names
// (cloud_build, render_manifests, wait_rollout, ...) are infrastructure
// jargon; nobody plugging an app should have to learn what `wait_settle`
// means. The step identity on the trace, in --json, and in the deploys.log
// is untouched — only this human-readable row label changes. Unknown steps
// fall back to `humanizeStep` so a new server step degrades to "Title case"
// instead of leaking snake_case. §23: plain, concrete, one word where it fits.
const STEP_LABELS: Record<string, string> = {
  parse_yaml: 'Reading config',
  cloud_build: 'Building',
  render_manifests: 'Preparing',
  apply_manifests: 'Deploying',
  gcp_identity: 'Linking identity',
  vault_resolve: 'Resolving secrets',
  caller_deps_resolve: 'Resolving deps',
  caller_secrets_write: 'Writing secrets',
  resolve_internal_urls: 'Wiring services',
  registry_write: 'Registering',
  wait_rollout: 'Starting',
  wait_settle: 'Stabilizing',
  http_probe: 'Health check',
  cert_provision: 'Provisioning cert',
  dns: 'Updating DNS',
  prune_orphans: 'Cleaning up',
  legacy_cutover: 'Migrating',
  legacy_finalize: 'Finalizing',
};

function humanizeStep(step: string): string {
  const spaced = step.replace(/_/g, ' ').trim();
  if (spaced.length === 0) return step;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Map a raw server step name to its builder-facing label. Exported so tests
 * can pin the wording and other renderers can stay consistent.
 */
export function stepLabel(step: string): string {
  return STEP_LABELS[step] ?? humanizeStep(step);
}

// Keys we don't want to echo in the human-facing extras string because
// they're already rendered elsewhere (marker, elapsed, error) or are pure
// infrastructure noise during a plug (`window_ms=30000` under wait_settle).
const EXTRA_SKIP_KEYS = new Set(['step', 'status', 'elapsed_ms', 'started_at', 'error', 'code', 'window_ms']);

function formatExtras(s: Step): string {
  const entries = Object.entries(s).filter(([k, v]) => !EXTRA_SKIP_KEYS.has(k) && v != null);
  return entries
    .map(([k, v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`)
    .join(' ');
}

function formatElapsed(ms: number | null | undefined): string {
  if (ms == null) return '';
  if (ms < 1000) return `${ms}ms`;
  const secs = Math.round(ms / 100) / 10;
  return `${secs}s`;
}

/**
 * Pure formatter — renders a steps array to a string. No I/O.
 * Exposed for testing and for programmatic renderers that don't need
 * TTY redraw state.
 *
 * Optional `childRows` map adds dim, indented child rows under matching
 * step rows. Used by 071-A to surface live Cloud Build progress under the
 * cloud_build spinner.
 */
export function renderSteps(
  steps: Step[],
  frameIdx = 0,
  childRows: Record<string, string[]> = {},
): string {
  return steps.map((s) => {
    const marker =
      s.status === 'ok'      ? chalk.green('✔') :
      s.status === 'failed'  ? chalk.red('✗') :
      s.status === 'skipped' ? chalk.dim('-') :
      chalk.cyan(SPINNER_FRAMES[frameIdx % SPINNER_FRAMES.length]);
    const name = stepLabel(s.step).padEnd(18);
    const elapsed = formatElapsed(s.elapsed_ms ?? null).padStart(6);
    const extras = formatExtras(s);
    const err = s.error ? `  ${chalk.red(s.error)}` : '';
    const row = `  ${marker} ${name} ${elapsed}  ${extras}${err}`;
    // Child rows (071-A live build progress) stay keyed by the raw step name —
    // only the displayed label changed, not the step's identity on the trace.
    const children = childRows[s.step];
    if (!children || children.length === 0) return row;
    const childLines = children.map((line) => `     ${chalk.dim(line)}`).join('\n');
    return `${row}\n${childLines}`;
  }).join('\n');
}

interface RendererOptions {
  /** Defaults to process.stdout.isTTY detection. */
  tty?: boolean;
  /** Used for testing. Defaults to process.stdout.write. */
  writer?: (s: string) => void;
  /**
   * Backlog 071-A — called on every TTY redraw frame to fetch live child
   * rows keyed by step name. Used by the Cloud Build progress tracker to
   * surface "what is the build doing right now?" under the cloud_build
   * spinner. CI mode does not consume this; child rows are TTY-only chrome.
   */
  getStepExtras?: () => Record<string, string[]>;
}

/**
 * Stateful renderer that switches between TTY in-place redraw and CI
 * append-only modes.
 */
export class MultiStepRenderer {
  private tty: boolean;
  private writer: (s: string) => void;
  private getStepExtras: (() => Record<string, string[]>) | null;
  private header = '';
  private lastSeenCount = 0;
  private frameTimer: ReturnType<typeof setInterval> | null = null;
  private currentSteps: Step[] = [];
  private frameIdx = 0;
  private started = false;

  constructor(opts: RendererOptions = {}) {
    this.tty = opts.tty ?? Boolean(process.stdout.isTTY);
    this.writer = opts.writer ?? ((s) => process.stdout.write(s + '\n'));
    this.getStepExtras = opts.getStepExtras ?? null;
  }

  start(header: string): void {
    this.header = header;
    this.started = true;
    if (this.tty) {
      logUpdate(`${header}\n`);
      this.frameTimer = setInterval(() => {
        this.frameIdx++;
        this.redraw();
      }, 80);
    } else {
      this.writer(header);
    }
  }

  update(steps: Step[]): void {
    this.currentSteps = steps;
    if (this.tty) {
      this.redraw();
      return;
    }
    // CI mode: append newly-completed steps only.
    // Walk from lastSeenCount and write each step that has left in_progress.
    // Stop at the first step still in_progress so we revisit it next call.
    let i = this.lastSeenCount;
    for (; i < steps.length; i++) {
      const s = steps[i];
      if (s.status === 'in_progress') break;
      this.writer(renderSteps([s]));
    }
    this.lastSeenCount = i;
  }

  finish(finalLine?: string): void {
    if (this.frameTimer) {
      clearInterval(this.frameTimer);
      this.frameTimer = null;
    }
    if (this.tty) {
      this.redraw();
      logUpdate.done();
    }
    if (finalLine) this.writer(finalLine);
  }

  private redraw(): void {
    if (!this.started) return;
    const extras = this.getStepExtras ? this.getStepExtras() : {};
    logUpdate(
      `${this.header}\n\n${renderSteps(this.currentSteps, this.frameIdx, extras)}`,
    );
  }
}
